import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { Link } from 'react-router-dom';
import './SubFolderDetails.css';
import './FolderUpload.css';
import { fetchGalleryList, fetchGalleryListByFolderID, fetchSelectedGalleryList } from '../../utils/s3';
import AWSGallery from '../Gallery/AWSGallery';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const PAGE_LIMIT = 10;

export default function SubFolderDetails({ eventId, userId }) {

  const { id, subFolder } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [maxKnownPage, setMaxKnownPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const getEffectiveIds = () => ({
    ev: eventId || localStorage.getItem('currentEventId') || '',
    uid: userId || localStorage.getItem('uid') || localStorage.getItem('vendorId') || '',
  });

  const fetchPage = async (pageNum, isReset = false) => {
    if (isReset) setLoading(true);
    try {
      const { ev, uid } = getEffectiveIds();
      if (!ev || !uid) return;

      let result;
      if (subFolder === "All") {
        result = await fetchGalleryList(ev, uid, pageNum, PAGE_LIMIT);
      } else if (subFolder === "Selected") {
        result = await fetchSelectedGalleryList(ev, uid, pageNum, PAGE_LIMIT);
      } else if (subFolder === "Seggregated Photos") {
        result = await fetchGalleryListByFolderID(ev, uid, id, pageNum, PAGE_LIMIT);
      }
      if (result) {
        setImages(result.photos);
        setPage(pageNum);
        setMaxKnownPage(prev => result.hasMore ? Math.max(prev, pageNum + 1) : Math.max(prev, pageNum));
      }
    } finally {
      if (isReset) setLoading(false);
    }
  };

  useEffect(() => {
    setImages([]);
    setPage(1);
    setMaxKnownPage(1);
    fetchPage(1, true);
  }, [subFolder, eventId, userId]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const { ev, uid } = getEffectiveIds();
      const allTitles = [];
      let p = 1;
      while (true) {
        const result = await fetchSelectedGalleryList(ev, uid, p, 100);
        (result.photos || []).forEach((photo, idx) => {
          const linkObj = Array.isArray(photo.link) ? photo.link[0] : photo.link;
          allTitles.push({ no: allTitles.length + 1, title: linkObj?.title || `Image ${allTitles.length + 1}` });
        });
        if (!result.hasMore) break;
        p++;
      }

      // Build minimal XLSX
      const xmlEscape = (s) => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
      const rows = [
        ['Sr. No.', 'Image Title'],
        ...allTitles.map(r => [r.no, r.title]),
      ];
      const sheetRows = rows.map(row =>
        `<row>${row.map((cell, ci) => {
          const ref = String.fromCharCode(65 + ci) + (rows.indexOf(row) + 1);
          const isNum = typeof cell === 'number';
          return `<c r="${ref}"${isNum ? '' : ' t="inlineStr"'}>${isNum ? `<v>${cell}</v>` : `<is><t>${xmlEscape(cell)}</t></is>`}</c>`;
        }).join('')}</row>`
      ).join('');

      const zip = new JSZip();
      zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`);
      zip.file('_rels/.rels', `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`);
      zip.file('xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Selected Photos" sheetId="1" r:id="rId1"/></sheets></workbook>`);
      zip.file('xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`);
      zip.file('xl/worksheets/sheet1.xml', `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRows}</sheetData></worksheet>`);

      const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'selected-photos.xlsx');
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const pageNumbers = Array.from({ length: maxKnownPage }, (_, i) => i + 1);

  return (
    <div className="sfd-page">

      <div className="fu-breadcrumb">
        <button className="fu-breadcrumb-back" onClick={() => { window.__appAllowBack = true; window.history.back(); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <Link to="/folders" className="fu-breadcrumb-link">All Folders</Link>
        <span className="fu-breadcrumb-sep">›</span>
        <span className="fu-breadcrumb-current">{subFolder}</span>
      </div>

      <div className="fu-header">
        <div className="fu-header-left">
          <h1>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            {subFolder === "All" ? "All Event Photos" : subFolder === "Selected" ? "Selected Photos" : subFolder}
          </h1>
          <p>
            {loading
              ? "Loading photos…"
              : images.length > 0
              ? `Page ${page} · ${images.length} photo${images.length !== 1 ? 's' : ''}`
              : "No photos uploaded yet"}
          </p>
        </div>
        {subFolder === "Selected" && !loading && images.length > 0 && (
          <button className="sfd-export-btn" onClick={handleExportExcel} disabled={exporting}>
            {exporting ? (
              <>
                <span className="sfd-export-spinner" />
                Exporting…
              </>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export to Excel
              </>
            )}
          </button>
        )}
      </div>

      {loading ? (
        <div className="sfd-empty">
          <div className="sfd-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <p className="sfd-empty-title">Loading photos…</p>
        </div>
      ) : images.length === 0 ? (
        <div className="sfd-empty">
          <div className="sfd-empty-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
          <h3 className="sfd-empty-title">No photos yet</h3>
          <p className="sfd-empty-sub">
            {subFolder === "All"
              ? "No photos have been uploaded for this event yet. Open a folder and upload images to see them here."
              : subFolder === "Selected"
              ? "No selected photos found for this event."
              : "No photos found in this folder."}
          </p>
          {subFolder === "All" && (
            <Link to="/folders" className="sfd-empty-btn">Go to Folders</Link>
          )}
        </div>
      ) : (
        <>
          <AWSGallery galleryData={images} />
          {maxKnownPage > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', margin: '24px 0', flexWrap: 'wrap' }}>
              <button
                onClick={() => fetchPage(page - 1)}
                disabled={page === 1}
                style={paginationBtnStyle(false, page === 1)}
              >
                ‹
              </button>
              {pageNumbers.map(num => (
                <button
                  key={num}
                  onClick={() => fetchPage(num)}
                  disabled={num === page}
                  style={paginationBtnStyle(num === page, false)}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={() => fetchPage(page + 1)}
                disabled={page === maxKnownPage}
                style={paginationBtnStyle(false, page === maxKnownPage)}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function paginationBtnStyle(isActive, isDisabled) {
  return {
    minWidth: '36px',
    height: '36px',
    padding: '0 10px',
    borderRadius: '6px',
    border: isActive ? '2px solid #F80353' : '1px solid #ddd',
    background: isActive ? '#F80353' : '#fff',
    color: isActive ? '#fff' : isDisabled ? '#bbb' : '#333',
    fontWeight: isActive ? '600' : '400',
    fontSize: '14px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
  };
}
